/**
 * @file useSidebarState - Hook for player kanban sidebar state management
 * @description Manages search, sort, filter state and player data processing for the player kanban sidebar.
 * This hook handles the creation of an immutable player lookup record mapping player IDs to their kanban
 * phase information, applies search and sort operations, and calculates sidebar statistics including
 * player counts, average GPR, and average contract values.
 * @remarks
 * The playersOnKanban lookup uses an immutable Record pattern (plain object) instead of Map for better
 * functional programming compliance and to avoid mutable operations. The reduce operation creates a new
 * object on each iteration rather than mutating an existing Map.
 */

import { useCallback, useMemo, useState } from "react";

import {
  DEFAULT_CONTRACT_VALUE_RANGE,
  DEFAULT_FILTER_STATE,
  type FilterState,
} from "../constants/filterDefaults";
import {
  getPlayerAge,
  getPlayerArchetype,
  getPlayerContractMonths,
  getPlayerTeamName,
} from "../utils/manualPlayerUtils";
import {
  shouldIncludePlayer,
  type PlayerFilterData,
} from "../utils/playerFilter";

import type { PlayerKanbanPlayerFragmentFragment } from "@/generated/graphql";
import { searchIncludes } from "@/utils/normalizeForSearch";

/**
 * Normalize a sidebar (watchlist/roster) player into the shape the board
 * filters operate on. Tags are intentionally empty: the filter targets the
 * unplaced pool, and card tags only exist for placed cards.
 * @param player - Sidebar player fragment
 * @returns Normalized filter data for the player
 */
const toFilterData = (
  player: PlayerKanbanPlayerFragmentFragment
): PlayerFilterData => {
  const min = player.bioData?.minGeminiPlayerValuation;
  const max = player.bioData?.maxGeminiPlayerValuation;
  const contractValueM =
    min != null && max != null ? (min + max) / 2 / 1_000_000 : undefined;
  return {
    id: player.id,
    archetype: getPlayerArchetype(player),
    age: getPlayerAge(player),
    gpr: player.bioData?.gpr ?? undefined,
    contractValueM,
    teamName: getPlayerTeamName(player),
    contractMonths: getPlayerContractMonths(player),
    tags: [],
  };
};

/**
 * Props for useSidebarState hook
 */
interface UseSidebarStateProps {
  /**
   * Array of processed Kanban player objects to display in sidebar
   */
  processedPlayers: PlayerKanbanPlayerFragmentFragment[];
  /**
   * Array of kanban card objects with player and phase information to track which players are on the board
   */
  kanbanCards: {
    /**
     * Player object containing the player ID
     */
    player: { id: string };
    /**
     * Kanban phase/column object containing phase ID and name
     */
    kanbanPhase: { id: string; name: string };
  }[];
  /**
   * Active board filters. Applied to the unplaced (sidebar) player pool so a
   * large watchlist/roster can be narrowed down. Placed cards on the board are
   * never affected by these filters. Defaults to the (unbounded) default state
   * so callers/tests that don't pass it get no filtering.
   */
  boardFilters?: FilterState;
  /**
   * When true, the contract-value (GPV) range was already applied server-side
   * by `listWatchlistPlayers` (SE-6496), so the client-side predicate is
   * neutralized. The server filters on the stored GPV midpoint and excludes
   * players without a GPV; re-running the client approximation here would
   * double-filter boundary values. Leave false for roster-sourced pools, which
   * have no server-side GPV filter.
   */
  contractValueFilteredServerSide?: boolean;
}

/**
 * Hook that handles search, sort, filter state and player data processing for the sidebar
 * @param root0 - The hook props
 * @param root0.processedPlayers - Array of processed Kanban player objects to search, sort, and calculate statistics for
 * @param root0.kanbanCards - Array of kanban card objects used to determine which players are currently on the board and their assigned phases
 * @param root0.boardFilters - Active board filters applied to the unplaced (left-panel) player pool
 * @param root0.contractValueFilteredServerSide - Whether the GPV range was already applied server-side (SE-6496)
 * @returns Object with search/sort state, filtered players, kanban lookup, stats, and change handlers
 */
const useSidebarState = ({
  processedPlayers,
  kanbanCards,
  boardFilters = DEFAULT_FILTER_STATE,
  contractValueFilteredServerSide = false,
}: UseSidebarStateProps): {
  searchQuery: string;
  sortBy: string;
  filteredPlayers: PlayerKanbanPlayerFragmentFragment[];
  playersOnKanban: Record<string, { phaseId: string; phaseName: string }>;
  stats: {
    totalPlayers: number;
    avgGpr: string;
    avgContract: string;
    activeCount: number;
    onKanban: number;
  };
  sortLabel: string;
  handleSearchChange: (query: string) => void;
  handleSortChange: () => void;
} => {
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("name");

  // Create a lookup map for players on kanban board
  const playersOnKanban = useMemo(() => {
    return kanbanCards.reduce(
      (lookup, card) => {
        if (card?.player?.id && card?.kanbanPhase) {
          return {
            ...lookup,
            [card.player.id]: {
              phaseId: card.kanbanPhase.id,
              phaseName: card.kanbanPhase.name,
            },
          };
        }
        return lookup;
      },
      {} as Record<string, { phaseId: string; phaseName: string }>
    );
  }, [kanbanCards]);

  // Apply search + board filters to players
  const filteredPlayers = useMemo(() => {
    // Tags are a placed-card concept — unplaced sidebar players never carry
    // card tags — so applying a tag filter here would empty the pool. Strip it.
    // The contract-value range is likewise neutralized when the server already
    // applied it (SE-6496).
    const sidebarFilters = {
      ...boardFilters,
      tags: [],
      ...(contractValueFilteredServerSide
        ? { contractValueRange: DEFAULT_CONTRACT_VALUE_RANGE }
        : {}),
    };
    const filtered = processedPlayers.filter(player => {
      // Board filters narrow the unplaced pool (Option 1). Placed cards always
      // render, so this only affects which players are offered in the left panel.
      if (!shouldIncludePlayer(toFilterData(player), sidebarFilters)) {
        return false;
      }

      // Filter by search query (accent-insensitive for SE-3556)
      const name = player.name ?? "";
      const archetype = getPlayerArchetype(player);
      const teamName = getPlayerTeamName(player);

      return (
        searchIncludes(name, searchQuery) ||
        searchIncludes(archetype, searchQuery) ||
        searchIncludes(teamName, searchQuery)
      );
    });

    // Sort players
    return [...filtered].sort((a, b) => {
      switch (sortBy) {
        case "name": {
          const nameA = a.name ?? "";
          const nameB = b.name ?? "";
          return nameA.localeCompare(nameB);
        }
        case "gpr": {
          const gprA = a.bioData?.gpr || 0;
          const gprB = b.bioData?.gpr || 0;
          return gprB - gprA;
        }
        case "age": {
          const ageA = getPlayerAge(a);
          const ageB = getPlayerAge(b);
          return ageA - ageB;
        }
        case "archetype": {
          const archetypeA = getPlayerArchetype(a);
          const archetypeB = getPlayerArchetype(b);
          return archetypeA.localeCompare(archetypeB);
        }
        default:
          return 0;
      }
    });
  }, [
    processedPlayers,
    searchQuery,
    sortBy,
    boardFilters,
    contractValueFilteredServerSide,
  ]);

  // Calculate stats
  const stats = useMemo(() => {
    if (filteredPlayers.length === 0) {
      return {
        totalPlayers: 0,
        avgGpr: "0.0",
        avgContract: "€0.0M",
        activeCount: 0,
        onKanban: 0,
      };
    }

    const validGprPlayers = filteredPlayers.filter(player => {
      const gpr = player.bioData?.gpr || 0;
      return gpr > 0;
    });

    const avgGpr =
      validGprPlayers.length > 0
        ? validGprPlayers.reduce((sum, player) => {
            const gpr = player.bioData?.gpr || 0;
            return sum + gpr;
          }, 0) / validGprPlayers.length
        : 0;

    // Calculate average contract value from players with Gemini valuation data
    const playersWithValuation = filteredPlayers.filter(
      player =>
        player.bioData?.minGeminiPlayerValuation != null &&
        player.bioData?.maxGeminiPlayerValuation != null
    );

    const avgContract =
      playersWithValuation.length > 0
        ? playersWithValuation.reduce((sum, player) => {
            const min = player.bioData?.minGeminiPlayerValuation || 0;
            const max = player.bioData?.maxGeminiPlayerValuation || 0;
            return sum + (min + max) / 2;
          }, 0) / playersWithValuation.length
        : 0;

    // Format average contract value
    const avgContractFormatted =
      avgContract >= 1000000
        ? `€${(avgContract / 1000000).toFixed(1)}M`
        : avgContract >= 1000
          ? `€${(avgContract / 1000).toFixed(0)}K`
          : "€0.0M";

    const onKanbanCount = filteredPlayers.filter(
      player => player.id in playersOnKanban
    ).length;

    return {
      totalPlayers: filteredPlayers.length,
      avgGpr: Math.round(avgGpr).toString(),
      avgContract: avgContractFormatted,
      activeCount: validGprPlayers.length,
      onKanban: onKanbanCount,
    };
  }, [filteredPlayers, playersOnKanban]);

  // Event handlers
  const handleSearchChange = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  const handleSortChange = useCallback(() => {
    const sortOptions = ["name", "gpr", "age", "archetype"];
    const currentIndex = sortOptions.indexOf(sortBy);
    const nextIndex = (currentIndex + 1) % sortOptions.length;
    setSortBy(sortOptions[nextIndex]);
  }, [sortBy]);

  const getSortLabel = useCallback(() => {
    switch (sortBy) {
      case "name":
        return "Sort: Name ↓";
      case "gpr":
        return "Sort: GPR ↓";
      case "age":
        return "Sort: Age ↑";
      case "archetype":
        return "Sort: Archetype ↓";
      default:
        return "Sort: Name ↓";
    }
  }, [sortBy]);

  return {
    // State
    searchQuery,
    sortBy,
    filteredPlayers,
    playersOnKanban,
    stats,

    // Computed
    sortLabel: getSortLabel(),

    // Handlers
    handleSearchChange,
    handleSortChange,
  };
};

export default useSidebarState;
